// Service pour interagir avec l'API de modules

import type { ModuleParameter, ApiModule, CreateModuleDto, UpdateModuleDto } from './types';

// Re-export pour compatibilité
export type { ModuleParameter, ApiModule, CreateModuleDto, UpdateModuleDto };

export class ModulesApiService {
    private baseUrl: string;
    private readToken: string;
    private writeToken: string;

    constructor(baseUrl: string, readToken: string, writeToken: string) {
        this.baseUrl = baseUrl;
        this.readToken = readToken;
        this.writeToken = writeToken;
    }

    /**
     * Récupère tous les modules depuis l'API
     */
    async getAllModules(): Promise<ApiModule[]> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/modules/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.readToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError('GET /api/v1/modules/', error);
            throw error;
        }
    }

    /**
     * Récupère un module spécifique par son ID
     */
    async getModuleById(moduleId: string): Promise<ApiModule> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/modules/${moduleId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.readToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`GET /api/v1/modules/${moduleId}`, error);
            throw error;
        }
    }

    /**
     * Crée un nouveau module
     */
    async createModule(module: CreateModuleDto): Promise<ApiModule> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/modules/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                },
                body: JSON.stringify(module)
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError('POST /api/v1/modules/', error);
            throw error;
        }
    }

    /**
     * Met à jour un module existant
     */
    async updateModule(moduleId: string, updates: UpdateModuleDto): Promise<ApiModule> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/modules/${moduleId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                },
                body: JSON.stringify(updates)
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`PUT /api/v1/modules/${moduleId}`, error);
            throw error;
        }
    }

    /**
     * Supprime un module
     */
    async deleteModule(moduleId: string): Promise<string> {
        try {
            const response = await fetch(`${this.baseUrl}/api/v1/modules/${moduleId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.writeToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            this.logError(`DELETE /api/v1/modules/${moduleId}`, error);
            throw error;
        }
    }

    /**
     * Logs une erreur (en production, écrit dans un fichier)
     */
    private logError(endpoint: string, error: unknown): void {
        const errorMessage = {
            timestamp: new Date().toISOString(),
            endpoint,
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined
        };

        // En mode développement, afficher dans la console
        console.error('❌ Erreur API Modules:', errorMessage);

        // Sauvegarder dans localStorage pour récupération ultérieure
        this.saveErrorToLocalStorage(errorMessage);
    }

    /**
     * Sauvegarde les erreurs dans le localStorage
     */
    private saveErrorToLocalStorage(error: any): void {
        try {
            const errors = JSON.parse(localStorage.getItem('gandc_api_errors') || '[]');
            errors.push(error);
            
            // Garder seulement les 100 dernières erreurs
            if (errors.length > 100) {
                errors.shift();
            }
            
            localStorage.setItem('gandc_api_errors', JSON.stringify(errors));
        } catch (e) {
            console.error('Impossible de sauvegarder les erreurs:', e);
        }
    }

    /**
     * Récupère les erreurs sauvegardées
     */
    static getStoredErrors(): any[] {
        try {
            return JSON.parse(localStorage.getItem('gandc_api_errors') || '[]');
        } catch (e) {
            return [];
        }
    }

    /**
     * Efface les erreurs sauvegardées
     */
    static clearStoredErrors(): void {
        localStorage.removeItem('gandc_api_errors');
    }

    /**
     * Télécharge les erreurs sous forme de fichier
     */
    static downloadErrorsAsFile(): void {
        const errors = ModulesApiService.getStoredErrors();
        const content = JSON.stringify(errors, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gandc_api_errors_${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}

